import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'

const APPROACHES = {
  lifestyle_first: {
    label: 'Lifestyle first',
    color: '#1D9E75',
    bg: '#E1F5EE',
    icon: '🏃',
    tagline: 'I prefer lifestyle changes before medications or procedures'
  },
  open_to_medications: {
    label: 'Open to medications',
    color: '#185FA5',
    bg: '#E6F1FB',
    icon: '💊',
    tagline: 'I am willing to take medications if lifestyle alone is not enough'
  },
  rhythm_control: {
    label: 'Rhythm control',
    color: '#534AB7',
    bg: '#EEEDFE',
    icon: '⚡',
    tagline: 'I am interested in antiarrhythmics and/or cardioversion'
  },
  open_to_ablation: {
    label: 'Open to ablation',
    color: '#BA7517',
    bg: '#FAEEDA',
    icon: '🏥',
    tagline: 'I am willing to consider catheter ablation'
  }
}

const CONTENT = {
  lifestyle_first: {
    evidence: [
      { title: 'Weight loss', text: 'Losing 10% or more of body weight reduces AFib burden up to four-fold. Even 3% loss can cut AFib episodes in half. This is one of the most powerful interventions available.' },
      { title: 'Exercise', text: 'Every 1 MET increase in cardiorespiratory fitness reduces AFib risk by 20%. Regular moderate exercise reduces AFib burden independently of weight loss.' },
      { title: 'Sleep apnea and CPAP', text: 'Treating sleep apnea with CPAP addresses several mechanisms that directly promote AFib — including drops in oxygen levels during sleep, abnormal activation of the nervous system, and large pressure swings in the chest that stress the heart\'s electrical system. Every night your CPAP works, it is protecting your heart rhythm.' },
      { title: 'Blood pressure control', text: 'High blood pressure is the single largest modifiable population-level risk factor for AFib. Lowering systolic blood pressure by just 10 mmHg reduces stroke risk by approximately 27% and cardiovascular mortality by 13%. Blood pressure control may be the highest-impact lifestyle intervention available outside of anticoagulation.' },
      { title: 'Alcohol reduction', text: 'Even modest alcohol reduction significantly reduces AFib episodes. AFib risk increases 8% for every additional drink per day. Binge drinking can trigger AFib episodes that last days.' },
      { title: 'Anticoagulation', text: 'Regardless of your care approach, stroke prevention is non-negotiable. AFib increases stroke risk 4-fold and 70% of first strokes from AFib are fatal or disabling. Discuss your CHA\u2082DS\u2082-VASc score with your provider.' },
    ],
    message: 'Lifestyle changes are not a consolation prize — they are powerful medicine. Patients who lose weight, exercise regularly, control their blood pressure, treat their sleep apnea, and reduce alcohol have dramatically fewer AFib episodes. Many have been able to reduce or eliminate medications through lifestyle alone. This is the foundation of AFib management regardless of what other treatments you pursue.',
    questions: [
      'How much weight loss would meaningfully reduce my AFib?',
      'What type and intensity of exercise is safe for me?',
      'Should I be tested for sleep apnea?',
      'What blood pressure target should I aim for?',
      'Do I need anticoagulation based on my stroke risk score?',
    ]
  },
  open_to_medications: {
    evidence: [
      { title: 'Rate control medications', text: 'Beta blockers, calcium channel blockers, and digoxin effectively reduce symptoms by slowing heart rate during AFib episodes. The RACE II trial showed that a lenient heart rate target (resting rate below 110) was non-inferior to strict rate control for clinical outcomes.' },
      { title: 'Anticoagulation — most important medication', text: 'Anticoagulation reduces stroke risk by 64% in AFib patients with risk factors. This is the most important medication decision in AFib regardless of symptoms. Modern blood thinners (DOACs) are safer and more convenient than warfarin for most patients.' },
      { title: 'Rate vs rhythm control', text: 'Rate control manages symptoms without restoring normal rhythm. It is a well-established, safe, and effective long-term strategy for many AFib patients, particularly those with persistent AFib or older age.' },
      { title: 'Lifestyle still matters', text: 'Medications work best when combined with lifestyle optimization. Weight loss, exercise, blood pressure control, and alcohol reduction all reduce AFib burden and may allow lower medication doses over time.' },
    ],
    message: 'Rate control medications help your heart beat more slowly and regularly during AFib episodes, reducing symptoms like palpitations, shortness of breath, and fatigue. While they do not restore normal rhythm, they allow many patients to live comfortably with AFib. Combined with anticoagulation to prevent stroke, rate control is a well-established, safe, and effective long-term strategy for many AFib patients.',
    questions: [
      'Am I a candidate for rate control rather than rhythm control?',
      'What heart rate target should I aim for?',
      'Which rate control medication is best for me given my other conditions?',
      'Do I need anticoagulation and which one is right for me?',
      'How will we know if medications are working well enough?',
    ]
  },
  rhythm_control: {
    evidence: [
      { title: 'EAST-AFNET 4 trial — landmark evidence', text: 'This landmark 2020 trial showed that early rhythm control reduced the composite of cardiovascular death, stroke, and hospitalization for heart failure or acute coronary syndrome by 21% compared to usual care. This is hard cardiovascular outcome data — not just symptom improvement.' },
      { title: 'ATHENA trial', text: 'Dronedarone reduced cardiovascular hospitalization and death in AFib patients with risk factors, further supporting rhythm control as a strategy with real outcome benefits.' },
      { title: 'Antiarrhythmic medications', text: 'Antiarrhythmic drugs including flecainide, propafenone, sotalol, amiodarone, and dronedarone can maintain normal sinus rhythm in 50-70% of patients at one year. Choice depends on your heart structure and other conditions.' },
      { title: 'Cardioversion', text: 'Electrical or chemical cardioversion rapidly restores normal rhythm and is often used alongside antiarrhythmics to maintain sinus rhythm. Most patients feel significantly better immediately after successful cardioversion.' },
      { title: 'Timing matters', text: 'The EAST-AFNET 4 trial showed the greatest benefit when rhythm control was initiated within 12 months of AFib diagnosis. Early intervention appears most effective.' },
    ],
    message: 'Maintaining normal heart rhythm — called sinus rhythm — has real cardiovascular benefits backed by hard outcome data. The landmark EAST-AFNET 4 trial showed that early rhythm control with antiarrhythmic medications reduced the risk of cardiovascular death, stroke, and heart failure hospitalization by 21%. This is compelling evidence that getting and staying in normal rhythm matters beyond just how you feel. Antiarrhythmic medications combined with cardioversion when needed is a well-supported strategy with proven benefits.',
    questions: [
      'Am I a good candidate for antiarrhythmic therapy?',
      'Which antiarrhythmic medication is appropriate for my heart condition?',
      'How often might I need cardioversion?',
      'What are the signs that my antiarrhythmic medication is not working?',
      'Should I start rhythm control early given the EAST-AFNET 4 data?',
    ]
  },
  open_to_ablation: {
    evidence: [
      { title: 'What ablation involves', text: 'Catheter ablation is an invasive cardiac procedure in which catheters are threaded through blood vessels into the heart to deliver energy that scars tissue responsible for triggering AFib — most commonly around the pulmonary veins.' },
      { title: 'Effectiveness', text: 'Ablation is more effective than antiarrhythmic medications at maintaining sinus rhythm, with 60-80% success at one year for paroxysmal AFib. Most patients experience meaningful improvement in symptoms and quality of life.' },
      { title: 'Complication risk', text: 'A large real-world analysis of Medicare patients found major complications — including cardiac tamponade, stroke, phrenic nerve injury, and death — occurred in approximately 7% of patients undergoing AFib ablation. This is an important consideration in the risk-benefit discussion with your provider.' },
      { title: 'Evidence limitations — CABANA trial', text: 'Ablation trials to date have not been sham-controlled, making it difficult to fully separate the procedural benefit from placebo effect. The landmark CABANA trial did not demonstrate a significant reduction in hard outcomes such as death or stroke in the overall intention-to-treat population, though quality of life improvements were consistently observed.' },
      { title: 'Lifestyle optimization improves outcomes', text: 'Weight loss, exercise, blood pressure control, and alcohol reduction before and after ablation significantly improve long-term success rates and reduce recurrence. Lifestyle optimization is essential regardless of whether ablation is chosen.' },
    ],
    message: 'Ablation is best viewed as a highly effective strategy for reducing AFib burden and improving quality of life in appropriately selected patients — not a cure. A large real-world analysis found major complications occurred in approximately 7% of Medicare patients undergoing the procedure. Lifestyle optimization before and after ablation, including weight loss, exercise, blood pressure control, and alcohol reduction, significantly improves long-term success rates and should be pursued regardless of whether ablation is chosen.',
    questions: [
      'Am I a good candidate for ablation given my type of AFib and heart structure?',
      'What is your center\'s success rate for ablation?',
      'What lifestyle changes should I make before ablation to improve my chances?',
      'What happens if AFib recurs after ablation?',
      'Should I try antiarrhythmic medications first?',
    ]
  }
}

export default function Education({ session, profile }) {
  const [expandedSection, setExpandedSection] = useState(null)
  const [expandedQuestion, setExpandedQuestion] = useState(false)
  const [selectedApproach, setSelectedApproach] = useState(null)

  useEffect(() => {
    if (profile?.care_approach) {
      setSelectedApproach(profile.care_approach)
    }
  }, [profile])

  const currentApproach = selectedApproach || 'lifestyle_first'
  const content = CONTENT[currentApproach]
  const approach = APPROACHES[currentApproach]

  return (
    <div style={{ fontFamily: 'Arial' }}>

      {/* My approach selector */}
      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
          My treatment approach
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {Object.entries(APPROACHES).map(([key, val]) => (
            <button key={key} onClick={() => setSelectedApproach(key)} style={{
              padding: '6px 12px', borderRadius: 20, border: 'none', fontSize: 12,
              cursor: 'pointer', fontWeight: selectedApproach === key ? 700 : 400,
              background: selectedApproach === key ? val.color : '#f0f4f8',
              color: selectedApproach === key ? 'white' : '#555'
            }}>
              {val.icon} {val.label}
              {profile?.care_approach === key && ' ✓'}
            </button>
          ))}
        </div>
        {profile?.care_approach !== selectedApproach && (
          <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
            Viewing content for {approach.label}. Your saved preference is {APPROACHES[profile?.care_approach]?.label || 'not set'}. Update your preference in the Profile tab.
          </div>
        )}
      </div>

      {/* Approach summary */}
      <div style={{
        background: approach.bg, borderRadius: 12, padding: '14px 16px',
        marginBottom: 10, borderLeft: `4px solid ${approach.color}`
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: approach.color, marginBottom: 6 }}>
          {approach.icon} {approach.label}
        </div>
        <div style={{ fontSize: 13, color: '#444', lineHeight: 1.6, marginBottom: 10 }}>
          {content.message}
        </div>
        <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic' }}>
          All approaches should include anticoagulation discussion and lifestyle optimization as a foundation.
        </div>
      </div>

      {/* Evidence sections */}
      <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Key evidence
      </div>
      {content.evidence.map((item, i) => (
        <div key={i} style={{ background: 'white', borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
          <div
            onClick={() => setExpandedSection(expandedSection === i ? null : i)}
            style={{ padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#2C2C2C' }}>{item.title}</div>
            <div style={{ fontSize: 16, color: '#888' }}>{expandedSection === i ? '▲' : '▼'}</div>
          </div>
          {expandedSection === i && (
            <div style={{ padding: '0 14px 14px', fontSize: 13, color: '#555', lineHeight: 1.7, borderTop: '0.5px solid #f0f0f0' }}>
              <br />{item.text}
            </div>
          )}
        </div>
      ))}

      {/* Questions to ask */}
      <div style={{ background: 'white', borderRadius: 12, marginBottom: 10, overflow: 'hidden' }}>
        <div
          onClick={() => setExpandedQuestion(!expandedQuestion)}
          style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#2C2C2C' }}>
            ❓ Questions to ask your provider
          </div>
          <div style={{ fontSize: 16, color: '#888' }}>{expandedQuestion ? '▲' : '▼'}</div>
        </div>
        {expandedQuestion && (
          <div style={{ padding: '0 16px 16px', borderTop: '0.5px solid #f0f0f0' }}>
            {content.questions.map((q, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: i < content.questions.length - 1 ? '0.5px solid #f0f0f0' : 'none', fontSize: 13, color: '#444', lineHeight: 1.5 }}>
                {i + 1}. {q}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AFib basics — always shown */}
      <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        AFib basics — for everyone
      </div>
      {[
        { title: 'What is AFib?', text: 'Atrial fibrillation is the most common sustained cardiac arrhythmia, affecting approximately 1% of the population and up to 10% of people over 75. The upper chambers of the heart beat chaotically instead of in a coordinated rhythm, which can cause symptoms and — most importantly — increases stroke risk 4-fold.' },
        { title: 'Why does stroke risk matter so much?', text: '70% of first strokes from AFib are fatal or disabling. The chaotic rhythm in AFib allows blood to pool in the left atrial appendage, where clots can form and travel to the brain. Anticoagulation prevents this and is the most important medication decision in AFib management.' },
        { title: 'What is CHA\u2082DS\u2082-VASc?', text: 'This is a validated scoring system that estimates your annual stroke risk based on risk factors: congestive heart failure, hypertension, age 65+, diabetes, prior stroke or TIA, vascular disease, and female sex. A score of 2 or more in men or 3 or more in women generally warrants anticoagulation. Your score is calculated live on your dashboard.' },
        { title: 'Can AFib be cured?', text: 'AFib tends to be a progressive condition — paroxysmal AFib (comes and goes) often progresses to persistent AFib over time without intervention. While no treatment is a guaranteed cure, aggressive lifestyle modification and early rhythm control can significantly slow or even reverse this progression in some patients.' },
      ].map((item, i) => (
        <div key={i} style={{ background: 'white', borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
          <div
            onClick={() => setExpandedSection(expandedSection === `basic${i}` ? null : `basic${i}`)}
            style={{ padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#2C2C2C' }}>{item.title}</div>
            <div style={{ fontSize: 16, color: '#888' }}>{expandedSection === `basic${i}` ? '▲' : '▼'}</div>
          </div>
          {expandedSection === `basic${i}` && (
            <div style={{ padding: '0 14px 14px', fontSize: 13, color: '#555', lineHeight: 1.7, borderTop: '0.5px solid #f0f0f0' }}>
              <br />{item.text}
            </div>
          )}
        </div>
      ))}

      <div style={{ fontSize: 11, color: '#888', textAlign: 'center', padding: '8px 0 16px', lineHeight: 1.6 }}>
        This educational content is for informational purposes only and does not constitute medical advice. Always discuss treatment decisions with your healthcare provider.
      </div>
    </div>
  )
}